import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Database } from '../../lib/database.types';
import { softDelete } from '../../lib/softDelete';
import { supabase } from '../../lib/supabase';

type TagRow = Database['public']['Tables']['tags']['Row'];
type TagInsert = Database['public']['Tables']['tags']['Insert'];
type TagUpdate = Database['public']['Tables']['tags']['Update'];

export type CatalogTagRow = Pick<TagRow, 'id' | 'name' | 'slug' | 'color' | 'created_at' | 'updated_at'>;

export interface TagInput {
  name: string;
  slug: string;
  color: string | null;
}

export const catalogTagsKey = (orgId: string | null) => ['catalog-tags', orgId] as const;

async function fetchTags(orgId: string): Promise<CatalogTagRow[]> {
  const { data, error } = await supabase
    .from('tags')
    .select('id, name, slug, color, created_at, updated_at')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('name', { ascending: true });
  if (error) throw error;
  return data;
}

export function useCatalogTags(orgId: string | null) {
  return useQuery({
    queryKey: catalogTagsKey(orgId),
    queryFn: () => (orgId ? fetchTags(orgId) : Promise.resolve<CatalogTagRow[]>([])),
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

function trimOrNull(v: string | null | undefined): string | null {
  const t = (v ?? '').trim();
  return t.length > 0 ? t : null;
}

export function useUpsertTag(orgId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: TagInput }) => {
      if (!orgId || !userId) throw new Error('Missing tag context');
      if (id) {
        const payload: TagUpdate = {
          name: values.name.trim(),
          slug: values.slug.trim(),
          color: trimOrNull(values.color),
        };
        const { error } = await supabase.from('tags').update(payload).eq('id', id);
        if (error) throw error;
        return;
      }
      const payload: TagInsert = {
        organization_id: orgId,
        name: values.name.trim(),
        slug: values.slug.trim(),
        color: trimOrNull(values.color),
        created_by: userId,
      };
      const { error } = await supabase.from('tags').insert(payload);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: catalogTagsKey(orgId) });
    },
  });
}

export function useSoftDeleteTag(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await softDelete('tags', id);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: catalogTagsKey(orgId) });
    },
  });
}
